import { useState } from 'react';
import { useParams, NavLink, Outlet } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { projectsApi } from '../../../api/projects';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

const TABS = [
  { to: 'overview', label: 'Overview' },
  { to: 'logical-framework', label: 'Logical Framework' },
  { to: 'workplan', label: 'Work Plan' },
  { to: 'budget', label: 'Budget' },
  { to: 'finance', label: 'Finance' },
  { to: 'beneficiaries', label: 'Beneficiaries' },
  { to: 'locations', label: 'Locations' },
  { to: 'implementation', label: 'Implementation' },
  { to: 'risk', label: 'Risk & Safeguarding' },
  { to: 'meal', label: 'Monitoring & MEAL' },
  { to: 'team', label: 'Team' },
  { to: 'approvals', label: 'Approvals & History' },
  { to: 'documents', label: 'Documents' },
  { to: 'amendments', label: 'Amendments' },
];

const STATUS_STYLES: Record<string, string> = {
  draft: 'status-badge-neutral', submitted: 'status-badge-warning', under_review: 'status-badge-info',
  approved: 'status-badge-success', in_progress: 'status-badge-info', completed: 'status-badge-success',
  rejected: 'status-badge-danger', closed: 'status-badge-neutral', cancelled: 'status-badge-danger',
  returned: 'status-badge-warning', clarification_required: 'status-badge-warning', archived: 'status-badge-neutral',
};

/**
 * Fetches the project ONCE at the workspace level and shares it with
 * every tab via route context (useOutletContext) - per the explicit
 * performance requirement to avoid redundant API calls across tabs
 * that all need the same project record.
 */
export function ProjectWorkspaceLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const id = Number(projectId);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const canSubmit = usePermission('projects.submit');
  const canClose = usePermission('projects.close');

  const projectQuery = useQuery({ queryKey: ['project', id], queryFn: () => projectsApi.get(id) });
  const pendingApprovalsQuery = useQuery({ queryKey: ['pending-approvals'], queryFn: () => projectsApi.pendingApprovals() });
  const myPendingApproval = pendingApprovalsQuery.data?.data.find((a) => a.record_id === id);

  const invalidateProject = () => queryClient.invalidateQueries({ queryKey: ['project', id] });

  const submitMutation = useMutation({
    mutationFn: () => projectsApi.submit(id),
    onSuccess: (result) => { showSuccess(`Submitted - ${result.data.approversAssigned} approver(s) notified.`); invalidateProject(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit project.'),
  });

  const closeMutation = useMutation({
    mutationFn: () => projectsApi.close(id),
    onSuccess: () => { showSuccess('Project closed.'); invalidateProject(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to close project.'),
  });

  const [decisionComment, setDecisionComment] = useState('');
  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => {
      if (!myPendingApproval) throw new Error('No pending approval found.');
      return projectsApi.decide(id, { approvalId: myPendingApproval.id, decision, comment: decisionComment || undefined });
    },
    onSuccess: (_r, decision) => {
      showSuccess(decision === 'approved' ? 'Project approved.' : 'Project rejected.');
      invalidateProject();
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      setDecisionComment('');
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  if (!projectQuery.data) {
    return <div className="page"><p className="muted">Loading project…</p></div>;
  }

  const project = projectQuery.data.data;
  const durationDays = Math.round((new Date(project.end_date).getTime() - new Date(project.start_date).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="page project-workspace">
      <section className="card project-header">
        <div className="project-header-top">
          <div>
            <h1>{project.project_name}</h1>
            <p className="muted">{project.project_reference}</p>
          </div>
          <span className={`status-badge ${STATUS_STYLES[project.status] ?? 'status-badge-neutral'}`}>
            PROJECT STATUS: {project.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        <div className="project-header-grid">
          <HeaderField label="Start Date" value={project.start_date} />
          <HeaderField label="End Date" value={project.end_date} />
          <HeaderField label="Duration" value={`${durationDays} days`} />
          <HeaderField label="Total Budget" value={`${project.currency_code} ${Number(project.budget_total).toLocaleString()}`} />
          <HeaderField label="Project Type" value={project.project_type} />
          <HeaderField label="Created" value={new Date(project.created_at).toLocaleDateString()} />
        </div>

        <div className="form-actions">
          {canSubmit && project.status === 'draft' && (
            <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
              {submitMutation.isPending ? 'Submitting…' : 'Submit for Review'}
            </button>
          )}
          {canClose && project.status === 'completed' && (
            <button onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending}>
              {closeMutation.isPending ? 'Closing…' : 'Close Project'}
            </button>
          )}
        </div>

        {myPendingApproval && (
          <div className="approval-panel">
            <h3>Your approval is pending on this project</h3>
            <p className="muted">Sequence {myPendingApproval.sequence} - decision required before this project can move forward.</p>
            <label>Comment (optional)<input value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} /></label>
            <div className="form-actions">
              <button onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}>
                <CheckCircle2 size={16} className="inline-icon" /> Approve
              </button>
              <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}>
                <XCircle size={16} className="inline-icon" /> Reject
              </button>
            </div>
          </div>
        )}
      </section>

      <nav className="workspace-tabs">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} className={({ isActive }) => 'workspace-tab' + (isActive ? ' workspace-tab-active' : '')}>
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <div className="workspace-tab-content">
        <Outlet context={{ project }} />
      </div>
    </div>
  );
}

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}
